import React, { useState, useEffect, useContext, useRef } from 'react';
import api from '../../api';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import Select from 'react-select';
import { 
  FaPlus, FaEdit, FaEye, FaSearch, FaTimes, FaTruck, FaCalendarAlt, 
  FaUser, FaShippingFast, FaPhone, FaIdCard, FaBox, FaCheckCircle, FaClock
} from 'react-icons/fa';

// Format number with commas
const formatNumberWithCommas = (number) => {
  const value = typeof number === 'string' ? parseFloat(number) : number;
  if (!value || isNaN(value)) return '0';
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(value);
};

// Initialize form state
const initializeFormState = (userId = '') => ({
  orderId: null,
  items: [],
  dispatchDate: new Date().toISOString().split('T')[0],
  transportDetails: {
    vehicleNumber: '',
    driverName: '',
    driverPhone: ''
  },
  expectedDeliveryDate: '',
  remarks: '',
  createdBy: userId
});

const TilesDispatchEntry = () => {
  const { user } = useContext(AuthContext);
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  // Sync dark mode with localStorage changes
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    const interval = setInterval(checkDarkMode, 500);
    return () => clearInterval(interval);
  }, []);

  // Data states
  const [orders, setOrders] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [dispatchId, setDispatchId] = useState(null);
  
  // Form state
  const [form, setForm] = useState(() => initializeFormState(user?.user?._id));
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Modal states
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [orderSearch, setOrderSearch] = useState('');
  
  // Fetch orders with pending dispatch quantities
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tiles-orders', {
        params: {
          company: user?.user?.company?._id ,
          status: 'pending,partially-dispatched'
        }
      });
      
      // Filter orders that have pending dispatch quantities
      const ordersWithPending = response.data.data.orders.filter(order => {
        return order.items.some(item => {
          const orderedQty = item.orderedQuantityBoxes || 0;
          const dispatchedQty = item.dispatchedQuantityBoxes || 0;
          const pending = orderedQty - dispatchedQty;
          return pending > 0;
        });
      });
      
      setOrders(ordersWithPending);
    } catch (error) {
      console.error('Error fetching orders:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch orders'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch dispatches
  const fetchDispatches = async () => {
    try {
      const response = await api.get('/tiles-dispatch', {
        params: { company: user?.user?.company?._id }
      });
      setDispatches(response.data.data.dispatches);
    } catch (error) {
      console.error('Error fetching dispatches:', error);
    }
  };
  
  useEffect(() => {
    if (user?.user?.company?._id) {
      fetchOrders();
      fetchDispatches();
    }
  }, [user]);
  
  // Handle order selection
  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    
    // Populate items with pending dispatch quantities
    const items = order.items
      .filter(item => {
        const pending = item.orderedQuantityBoxes - (item.dispatchedQuantityBoxes || 0);
        return pending > 0;
      })
      .map(item => ({
        tileId: item.tileId._id,
        tilesName: item.tileId.tilesName || item.tileId.displayName,
        orderedBoxes: item.orderedQuantityBoxes,
        dispatchedBoxes: item.dispatchedQuantityBoxes || 0,
        pendingBoxes: item.orderedQuantityBoxes - (item.dispatchedQuantityBoxes || 0),
        dispatchingBoxes: 0 // User will fill this
      }));
    
    setForm(prev => ({
      ...prev,
      orderId: order._id,
      items: items,
      expectedDeliveryDate: order.expectedDeliveryDate ? 
        new Date(order.expectedDeliveryDate).toISOString().split('T')[0] : ''
    }));
    
    setShowOrderModal(false);
  };
  
  // Handle item quantity change
  const handleItemQuantityChange = (index, value) => {
    const numValue = parseInt(value) || 0;
    const item = form.items[index];
    
    if (numValue > item.pendingBoxes) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Quantity',
        html: `<div style="text-align: left;">
          <p>Cannot dispatch <strong>${numValue}</strong> boxes for:</p>
          <p><strong>${item.tilesName}</strong></p>
          <p class="text-red-600 mt-2">Only <strong>${item.pendingBoxes}</strong> boxes available for dispatch.</p>
        </div>`,
        confirmButtonColor: '#3085d6'
      });
      return;
    }
    
    const updatedItems = [...form.items];
    updatedItems[index].dispatchingBoxes = numValue;
    setForm(prev => ({ ...prev, items: updatedItems }));
  };
  
  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.orderId) {
      Swal.fire({
        icon: 'warning',
        title: 'Order Required',
        text: 'Please select an order for dispatch'
      });
      return;
    }
    
    const itemsToDispatch = form.items.filter(item => item.dispatchingBoxes > 0);
    
    if (itemsToDispatch.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Items Selected',
        text: 'Please enter dispatch quantities for at least one item'
      });
      return;
    }
    
    try {
      setSubmitting(true);
      
      const payload = {
        orderId: form.orderId,
        items: itemsToDispatch.map(item => ({
          tileId: item.tileId,
          dispatchedBoxes: item.dispatchingBoxes
        })),
        dispatchDate: form.dispatchDate,
        transportDetails: form.transportDetails,
        expectedDeliveryDate: form.expectedDeliveryDate || null,
        remarks: form.remarks,
        createdBy: user?.user?._id
      };
      
      if (isEditing && dispatchId) {
        await api.put(`/tiles-dispatch/${dispatchId}`, payload);
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Dispatch updated successfully',
          timer: 2000
        });
      } else {
        await api.post('/tiles-dispatch', payload);
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Dispatch created successfully',
          timer: 2000
        });
      }
      
      // Reset form
      handleReset();
      fetchOrders();
      fetchDispatches();
      
    } catch (error) {
      console.error('Error submitting dispatch:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        html: `<div style="text-align: left;">
          <p>${error.response?.data?.message || 'Failed to create dispatch'}</p>
        </div>`
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle reset
  const handleReset = () => {
    setForm(initializeFormState(user?.user?._id));
    setSelectedOrder(null);
    setIsEditing(false);
    setDispatchId(null);
  };
  
  // Handle view dispatch
  const handleViewDispatch = async (dispatch) => {
    try {
      const response = await api.get(`/tiles-dispatch/${dispatch._id}`);
      setSelectedDispatch(response.data.data);
      setShowViewModal(true);
    } catch (error) {
      console.error('Error fetching dispatch details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch dispatch details'
      });
    }
  };
  
  // Handle edit dispatch
  const handleEditDispatch = async (dispatch) => {
    try {
      const response = await api.get(`/tiles-dispatch/${dispatch._id}`);
      const dispatchData = response.data.data;
      
      // Get order details
      const orderResponse = await api.get(`/tiles-orders/${dispatchData.orderId._id}`);
      const order = orderResponse.data.data;
      
      setSelectedOrder(order);
      setDispatchId(dispatch._id);
      setIsEditing(true);
      
      // Populate form with dispatch data
      const items = dispatchData.items.map(item => {
        const orderItem = order.items.find(oi => oi.tileId._id === item.tileId._id);
        const pendingBoxes = orderItem ? 
          orderItem.orderedQuantityBoxes - ((orderItem.dispatchedQuantityBoxes || 0) - item.dispatchedBoxes) : 
          0;
        
        return {
          tileId: item.tileId._id,
          tilesName: item.tileId.tilesName || item.tileId.displayName,
          orderedBoxes: orderItem?.orderedQuantityBoxes || 0,
          dispatchedBoxes: (orderItem?.dispatchedQuantityBoxes || 0) - item.dispatchedBoxes,
          pendingBoxes: pendingBoxes + item.dispatchedBoxes,
          dispatchingBoxes: item.dispatchedBoxes
        };
      });
      
      setForm({
        orderId: dispatchData.orderId._id,
        items: items,
        dispatchDate: new Date(dispatchData.dispatchDate).toISOString().split('T')[0],
        transportDetails: dispatchData.transportDetails || {
          vehicleNumber: '',
          driverName: '',
          driverPhone: ''
        },
        expectedDeliveryDate: dispatchData.expectedDeliveryDate ? 
          new Date(dispatchData.expectedDeliveryDate).toISOString().split('T')[0] : '',
        remarks: dispatchData.remarks || '',
        createdBy: user?.user?._id
      });
      
      // Scroll to form
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Error loading dispatch for edit:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load dispatch details'
      });
    }
  };
  
  // Filter orders for modal
  const filteredOrders = orders.filter(order => {
    const searchLower = orderSearch.toLowerCase();
    return (
      order.orderId.toLowerCase().includes(searchLower) ||
      order.supplier?.name?.toLowerCase().includes(searchLower)
    );
  });
  
  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      'in-transit': { color: darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800', icon: FaTruck, label: 'In Transit' },
      'pending': { color: darkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800', icon: FaClock, label: 'Pending' },
      'partial': { color: darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800', icon: FaBox, label: 'Partial' },
      'delivered': { color: darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800', icon: FaCheckCircle, label: 'Delivered' },
      'cancelled': { color: darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800', icon: FaTimes, label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className={`rounded-2xl shadow-2xl p-8 mb-8 ${darkMode ? 'bg-gradient-to-r from-indigo-800 to-purple-800' : 'bg-gradient-to-r from-indigo-600 to-purple-600'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                <FaShippingFast className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Tiles Dispatch Entry</h1>
                <p className={darkMode ? 'text-indigo-200' : 'text-indigo-100'}>Create and manage dispatch entries for ordered tiles</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className={`rounded-2xl shadow-xl p-8 mb-8 transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h2 className={`text-2xl font-bold mb-6 flex items-center gap-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            <div className="bg-gradient-to-br from-indigo-500 to-purple-500 p-2 rounded-lg">
              <FaPlus className="w-5 h-5 text-white" />
            </div>
            {isEditing ? 'Edit Dispatch Entry' : 'Create New Dispatch Entry'}
          </h2>

          <form onSubmit={handleSubmit}>
            {/* Order Selection */}
            <div className="mb-6">
              <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Select Order <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowOrderModal(true)}
                  className={`w-full px-4 py-3 border-2 rounded-xl text-left transition-all duration-200 flex items-center justify-between group ${darkMode ? 'bg-gray-700 border-indigo-700 hover:border-indigo-500' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-indigo-200 hover:border-indigo-400'}`}
                >
                  <span className={darkMode ? 'text-gray-200' : 'text-gray-700'}>
                    {selectedOrder ? (
                      <span className="flex items-center gap-2">
                        <FaBox className={darkMode ? 'text-indigo-400' : 'text-indigo-600'} />
                        <strong>{selectedOrder.orderId}</strong>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>- {selectedOrder.supplier?.name}</span>
                      </span>
                    ) : (
                      <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Click to select order...</span>
                    )}
                  </span>
                  <FaSearch className={`${darkMode ? 'text-indigo-400' : 'text-indigo-600'} group-hover:scale-110 transition-transform`} />
                </button>
              </div>
            </div>

            {/* Items Table */}
            {form.items.length > 0 && (
              <div className="mb-6">
                <label className={`block text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Dispatch Items <span className="text-red-500">*</span>
                </label>
                <div className={`overflow-x-auto rounded-xl border-2 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <table className="w-full">
                    <thead className={darkMode ? 'bg-gradient-to-r from-indigo-800 to-purple-800 text-white' : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white'}>
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">#</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Tile Name</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Ordered</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Dispatched</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Pending</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold">Dispatch Now</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                      {form.items.map((item, index) => (
                        <tr key={index} className={`transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-indigo-50'}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white font-bold text-sm">
                              {index + 1}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.tilesName}</div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-block px-3 py-1 rounded-lg font-semibold ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>                              {formatNumberWithCommas(item.orderedBoxes)} boxes
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-block px-3 py-1 rounded-lg font-semibold ${darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                              {formatNumberWithCommas(item.dispatchedBoxes)} boxes
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-block px-3 py-1 rounded-lg font-semibold ${darkMode ? 'bg-amber-900 text-amber-300' : 'bg-amber-100 text-amber-800'}`}>
                              {formatNumberWithCommas(item.pendingBoxes)} boxes
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="0"
                              max={item.pendingBoxes}
                              value={item.dispatchingBoxes}
                              onChange={(e) => handleItemQuantityChange(index, e.target.value)}
                              className={`w-32 px-3 py-2 border-2 rounded-lg text-center font-semibold focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all ${darkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300 bg-white'}`}
                              placeholder="0"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Dispatch Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCalendarAlt className={`inline mr-2 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                  Dispatch Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={form.dispatchDate}
                  onChange={(e) => setForm(prev => ({ ...prev, dispatchDate: e.target.value }))}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white'}`}
                  required
                />
              </div>
              
              <div>
                <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FaCalendarAlt className={`inline mr-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                  Expected Delivery Date
                </label>
                <input
                  type="date"
                  value={form.expectedDeliveryDate}
                  onChange={(e) => setForm(prev => ({ ...prev, expectedDeliveryDate: e.target.value }))}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white'}`}
                />
              </div>
            </div>

            {/* Transport Details */}
            <div className={`rounded-xl p-6 mb-6 transition-colors duration-300 ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
              <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <FaTruck className={darkMode ? 'text-indigo-400' : 'text-indigo-600'} />
                Transport Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaIdCard className={`inline mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    Vehicle Number
                  </label>
                  <input
                    type="text"
                    value={form.transportDetails.vehicleNumber}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      transportDetails: { ...prev.transportDetails, vehicleNumber: e.target.value }
                    }))}
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all ${darkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300 bg-white'}`}
                    placeholder="e.g., MH12AB1234"
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaUser className={`inline mr-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                    Driver Name
                  </label>
                  <input
                    type="text"
                    value={form.transportDetails.driverName}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      transportDetails: { ...prev.transportDetails, driverName: e.target.value }
                    }))}
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all ${darkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300 bg-white'}`}
                    placeholder="Driver name"
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaPhone className={`inline mr-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                    Driver Phone
                  </label>
                  <input
                    type="tel"
                    value={form.transportDetails.driverPhone}
                    onChange={(e) => setForm(prev => ({
                      ...prev,
                      transportDetails: { ...prev.transportDetails, driverPhone: e.target.value }
                    }))}
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all ${darkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300 bg-white'}`}
                    placeholder="Phone number"
                  />
                </div>
              </div>
            </div>

            {/* Remarks */}
            <div className="mb-6">
              <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Remarks
              </label>
              <textarea
                value={form.remarks}
                onChange={(e) => setForm(prev => ({ ...prev, remarks: e.target.value }))}
                rows="3"
                className={`w-full px-4 py-3 border-2 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all resize-none ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white'}`}
                placeholder="Enter any additional notes or comments..."
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={submitting || !form.orderId}
                className={`flex-1 text-white px-6 py-4 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 ${darkMode ? 'bg-gradient-to-r from-indigo-700 to-purple-700 hover:from-indigo-600 hover:to-purple-600' : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'}`}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <FaShippingFast className="w-5 h-5" />
                    <span>{isEditing ? 'Update Dispatch' : 'Create Dispatch'}</span>
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={handleReset}
                className={`px-6 py-4 border-2 rounded-xl font-semibold transition-all duration-200 flex items-center gap-2 ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              >
                <FaTimes className="w-5 h-5" />
                <span>Reset</span>
              </button>
            </div>
          </form>
        </div>

        {/* Dispatches List */}
        <div className={`rounded-2xl shadow-xl p-8 transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <h2 className={`text-2xl font-bold mb-6 flex items-center gap-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            <div className="bg-gradient-to-br from-green-500 to-teal-500 p-2 rounded-lg">
              <FaTruck className="w-5 h-5 text-white" />
            </div>
            Dispatch Entries
          </h2>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${darkMode ? 'border-indigo-400' : 'border-indigo-600'}`} />
            </div>
          ) : dispatches.length === 0 ? (
            <div className="text-center py-12">
              <FaTruck className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No dispatch entries found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`border-b-2 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'}`}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Dispatch ID</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Order ID</th>
                    <th className={`px-4 py-3 text-left text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Supplier</th>
                    <th className={`px-4 py-3 text-center text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Dispatch Date</th>
                    <th className={`px-4 py-3 text-center text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</th>
                    <th className={`px-4 py-3 text-center text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                  {dispatches.map((dispatch) => (
                    <tr key={dispatch._id} className={`transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                      <td className="px-4 py-4">
                        <span className={`font-semibold ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{dispatch.dispatchId}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={darkMode ? 'text-gray-200' : 'text-gray-700'}>{dispatch.orderId?.orderId}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={darkMode ? 'text-gray-200' : 'text-gray-700'}>{dispatch.supplier?.name}</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                          {new Date(dispatch.dispatchDate).toLocaleDateString('en-IN')}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <StatusBadge status={dispatch.status} />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleViewDispatch(dispatch)}
                            className={`p-2 rounded-lg transition-colors ${darkMode ? 'bg-blue-900 text-blue-300 hover:bg-blue-800' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}
                            title="View Details"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                          {dispatch.status === 'pending' && (
                            <button
                              onClick={() => handleEditDispatch(dispatch)}
                              className={`p-2 rounded-lg transition-colors ${darkMode ? 'bg-amber-900 text-amber-300 hover:bg-amber-800' : 'bg-amber-100 text-amber-600 hover:bg-amber-200'}`}
                              title="Edit Dispatch"
                            >
                              <FaEdit className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Order Selection Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 flex items-center justify-between ${darkMode ? 'bg-gradient-to-r from-indigo-800 to-purple-800' : 'bg-gradient-to-r from-indigo-600 to-purple-600'}`}>
              <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                <FaBox className="w-6 h-6" />
                Select Order for Dispatch
              </h3>
              <button
                onClick={() => setShowOrderModal(false)}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <input
                  type="text"
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="Search by Order ID or Supplier..."
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white'}`}
                />
              </div>
              
              <div className="max-h-96 overflow-y-auto space-y-2">
                {filteredOrders.length === 0 ? (
                  <div className="text-center py-12">
                    <FaBox className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>No orders with pending dispatch found</p>
                  </div>
                ) : (
                  filteredOrders.map((order) => (
                    <button
                      key={order._id}
                      onClick={() => handleOrderSelect(order)}
                      className={`w-full p-4 border-2 rounded-xl transition-all text-left ${darkMode ? 'border-gray-600 hover:border-indigo-500 hover:bg-gray-700' : 'border-gray-200 hover:border-indigo-500 hover:bg-indigo-50'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-bold text-lg ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{order.orderId}</span>
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {new Date(order.orderDate).toLocaleDateString('en-IN')}
                        </span>
                      </div>
                      <div className={`mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        <strong>Supplier:</strong> {order.supplier?.name}
                      </div>
                      <div className="flex gap-2 text-sm">
                        <span className={`px-2 py-1 rounded-lg font-semibold ${darkMode ? 'bg-amber-900 text-amber-300' : 'bg-amber-100 text-amber-800'}`}>
                          {order.items.filter(i => (i.orderedQuantityBoxes - (i.dispatchedQuantityBoxes || 0)) > 0).length} items pending
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Dispatch Modal */}
      {showViewModal && selectedDispatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 flex items-center justify-between sticky top-0 ${darkMode ? 'bg-gradient-to-r from-blue-800 to-indigo-800' : 'bg-gradient-to-r from-blue-600 to-indigo-600'}`}>
              <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                <FaEye className="w-6 h-6" />
                Dispatch Details
              </h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
                  <div className={`text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Dispatch ID</div>
                  <div className={`font-bold text-lg ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>{selectedDispatch.dispatchId}</div>
                </div>
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-purple-50 to-pink-50'}`}>
                  <div className={`text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Order ID</div>
                  <div className={`font-bold text-lg ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>{selectedDispatch.orderId?.orderId}</div>
                </div>
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-green-50 to-teal-50'}`}>
                  <div className={`text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status</div>
                  <StatusBadge status={selectedDispatch.status} />
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-amber-50 to-yellow-50'}`}>
                  <div className={`text-sm mb-1 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <FaCalendarAlt className={darkMode ? 'text-amber-400' : 'text-amber-600'} />
                    Dispatch Date
                  </div>
                  <div className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {new Date(selectedDispatch.dispatchDate).toLocaleDateString('en-IN')}
                  </div>
                </div>
                {selectedDispatch.expectedDeliveryDate && (
                  <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-green-50 to-emerald-50'}`}>
                    <div className={`text-sm mb-1 flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <FaCalendarAlt className={darkMode ? 'text-green-400' : 'text-green-600'} />
                      Expected Delivery
                    </div>
                    <div className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {new Date(selectedDispatch.expectedDeliveryDate).toLocaleDateString('en-IN')}
                    </div>
                  </div>
                )}
              </div>

              {/* Transport Details */}
              {(selectedDispatch.transportDetails?.vehicleNumber || 
                selectedDispatch.transportDetails?.driverName ||
                selectedDispatch.transportDetails?.driverPhone) && (
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
                  <h4 className={`font-bold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    <FaTruck className={darkMode ? 'text-indigo-400' : 'text-indigo-600'} />
                    Transport Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {selectedDispatch.transportDetails?.vehicleNumber && (
                      <div>
                        <div className={`text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Vehicle Number</div>
                        <div className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {selectedDispatch.transportDetails.vehicleNumber}
                        </div>
                      </div>
                    )}
                    {selectedDispatch.transportDetails?.driverName && (
                      <div>
                        <div className={`text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Driver Name</div>
                        <div className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {selectedDispatch.transportDetails.driverName}
                        </div>
                      </div>
                    )}
                    {selectedDispatch.transportDetails?.driverPhone && (
                      <div>
                        <div className={`text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Driver Phone</div>
                        <div className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {selectedDispatch.transportDetails.driverPhone}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Items */}
              <div>
                <h4 className={`font-bold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  <FaBox className={darkMode ? 'text-indigo-400' : 'text-indigo-600'} />
                  Dispatched Items
                </h4>
                <div className="space-y-2">
                  {selectedDispatch.items.map((item, index) => (
                    <div key={index} className={`border-2 rounded-xl p-4 transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 hover:border-indigo-500' : 'bg-white border-gray-200 hover:border-indigo-300'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white font-bold text-sm">
                            {index + 1}
                          </div>
                          <div>
                            <div className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {item.tileId?.tilesName || item.tileId?.displayName}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                            {formatNumberWithCommas(item.dispatchedBoxes)}
                          </div>
                          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>boxes dispatched</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Remarks */}
              {selectedDispatch.remarks && (
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-br from-amber-50 to-orange-50'}`}>
                  <h4 className={`font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Remarks</h4>
                  <p className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{selectedDispatch.remarks}</p>
                </div>
              )}

              {/* Metadata */}
              <div className={`p-4 rounded-xl text-sm ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                <div className="flex items-center justify-between">
                  <span>Created by: <strong className={darkMode ? 'text-gray-200' : 'text-gray-800'}>{selectedDispatch.createdBy?.name}</strong></span>
                  <span>
                    {new Date(selectedDispatch.createdAt).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>

            <div className={`p-6 border-t flex justify-end ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowViewModal(false)}
                className={`px-6 py-3 rounded-xl font-semibold transition-colors ${darkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-600 text-white hover:bg-gray-700'}`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TilesDispatchEntry;
